import {
  first_value,
  title_case,
  get_verification_status_tone,
  get_verification_impact,
  format_log_timestamp,
  get_statuses_for_form,
  build_service_name_map,
  resolve_service_from_candidates,
  get_case_identifier,
  get_case_name
} from './onboarding_verification_common_helpers';
import { append_auto_ledger_checks } from './onboarding_verification_ledger_helpers';

function get_service_id(service = {}) {
  return Number(service?.id || service?.service_id || 0);
}

function build_service_map(services = []) {
  const entries = (Array.isArray(services) ? services : []).map(function service_entry(service) {
    return [String(service.id), service];
  });

  return new Map(entries);
}

function get_configured_services(bound_services = []) {
  return Array.isArray(bound_services) ? bound_services : [];
}

function get_normalized_forms(forms = []) {
  return Array.isArray(forms) ? forms : [];
}

function resolve_service_id(service_id, service) {
  return Number(first_value([
    service_id,
    service?.id,
    service?.service_id
  ], 0)) || null;
}

function build_status_check(context, form, service_id, status_record) {
  const service = resolve_service_from_candidates({
    service_map: context.service_map,
    service_name_map: context.service_name_map,
    service_id,
    service_name: status_record?.service_name
  });
  const application_form_id = first_value([form?.application_form_id, form?.id], null);
  const resolved_service_id = resolve_service_id(service_id, service);
  const tone = get_verification_status_tone(status_record?.status);
  const case_identifier = get_case_identifier(context.case_item);

  return {
    id: `${case_identifier}-${application_form_id}-${resolved_service_id || service_id}`,
    check: first_value([status_record?.service_name, service?.name], 'Verification check'),
    case_name: get_case_name(context.case_item),
    source: first_value([service?.description, service?.name, status_record?.service_name], 'Backend verification'),
    status: title_case(status_record?.status || 'Pending'),
    status_tone: tone,
    impact: get_verification_impact(tone),
    last_run: format_log_timestamp(status_record?.timestamp),
    application_form_id,
    service_id: resolved_service_id,
    case_item: context.case_item,
    raw_status: status_record,
    verification_mode: 'auto',
    is_runnable: Boolean(application_form_id && resolved_service_id),
    service,
    history: []
  };
}

function add_status_check(context, form, service_id, status_record) {
  const check = build_status_check(context, form, service_id, status_record);
  const key = `${check.application_form_id || 'none'}:${check.service_id || service_id}`;

  context.configured_service_ids.add(`${check.application_form_id}:${check.service_id}`);
  context.checks.push(check);
  context.checks_by_history_key.set(key, check);
}

function add_form_status_checks(context, form) {
  const application_form_id = first_value([form?.application_form_id, form?.id], null);
  const statuses = get_statuses_for_form(context.verification_statuses_by_form, application_form_id);

  Object.entries(statuses).forEach(function add_status_entry(entry) {
    add_status_check(context, form, entry[0], entry[1]);
  });
}

function build_pending_check(context, application_form_id, service) {
  const service_id = get_service_id(service);
  const case_identifier = get_case_identifier(context.case_item);

  return {
    id: `${case_identifier}-${application_form_id}-${service_id}-pending`,
    check: first_value([service?.name], 'Verification check'),
    case_name: get_case_name(context.case_item),
    source: first_value([service?.description, service?.name], 'Backend verification'),
    status: 'Not Run',
    status_tone: 'secondary',
    impact: 'Ready to run',
    last_run: '-',
    application_form_id,
    service_id,
    case_item: context.case_item,
    raw_status: null,
    verification_mode: 'auto',
    is_runnable: true,
    service,
    history: []
  };
}

function should_add_pending_check(context, application_form_id, service_id) {
  const check_key = `${application_form_id}:${service_id}`;
  return Boolean(application_form_id && service_id && !context.configured_service_ids.has(check_key));
}

function add_pending_service_check(context, application_form_id, service) {
  const service_id = get_service_id(service);
  if (!should_add_pending_check(context, application_form_id, service_id)) {
    return;
  }

  const check = build_pending_check(context, application_form_id, service);
  context.checks.push(check);
  context.checks_by_history_key.set(`${application_form_id || 'none'}:${service_id}`, check);
}

function add_pending_form_checks(context, form) {
  const application_form_id = first_value([form?.application_form_id, form?.id], null);

  context.configured_services.forEach(function add_service(service) {
    add_pending_service_check(context, application_form_id, service);
  });
}

function build_history_entry(context, log, index, tone) {
  const case_identifier = get_case_identifier(context.case_item);

  return {
    id: `${case_identifier}-log-${log?.id || index}`,
    status: title_case(log?.status || 'Pending'),
    status_tone: tone,
    detail: first_value([log?.message, get_verification_impact(tone)], 'Recorded'),
    last_run: format_log_timestamp(log?.created_at),
    raw_log: log
  };
}

function add_log_check(context, log, history_entry, service, ids) {
  context.checks.push({
    id: history_entry.id,
    check: first_value([service?.name, `Verification ${log?.service_id || ''}`], 'Verification check'),
    case_name: get_case_name(context.case_item),
    source: first_value([service?.description, service?.name], 'Backend verification'),
    status: history_entry.status,
    status_tone: history_entry.status_tone,
    impact: history_entry.detail,
    last_run: history_entry.last_run,
    application_form_id: ids.application_form_id,
    service_id: ids.service_id,
    case_item: context.case_item,
    raw_log: log,
    verification_mode: 'auto',
    is_runnable: Boolean(ids.application_form_id && ids.service_id),
    service,
    history: [history_entry]
  });
}

function add_verification_log(context, log, index) {
  const service = resolve_service_from_candidates({
    service_map: context.service_map,
    service_name_map: context.service_name_map,
    service_id: log?.service_id,
    service_name: log?.service_name || log?.service
  });
  const tone = get_verification_status_tone(log?.status);
  const ids = {
    application_form_id: first_value([log?.application_form_id, log?.form_id], null),
    service_id: resolve_service_id(log?.service_id, service)
  };
  const history_entry = build_history_entry(context, log, index, tone);
  const history_key = `${ids.application_form_id || 'none'}:${ids.service_id}`;
  const matched_check = context.checks_by_history_key.get(history_key);

  if (matched_check) {
    matched_check.history = [...matched_check.history, history_entry];
    return;
  }

  add_log_check(context, log, history_entry, service, ids);
}

function build_bound_service_check(context, service) {
  const service_id = get_service_id(service);
  const case_identifier = get_case_identifier(context.case_item);

  return {
    id: `${case_identifier}-bound-service-${service_id}`,
    check: first_value([service?.name], 'Verification check'),
    case_name: get_case_name(context.case_item),
    source: first_value([service?.description, service?.name], 'Backend verification'),
    status: 'Not Run',
    status_tone: 'secondary',
    impact: 'Ready to run',
    last_run: '-',
    application_form_id: null,
    service_id,
    case_item: context.case_item,
    raw_status: null,
    verification_mode: 'auto',
    is_runnable: true,
    service,
    history: []
  };
}

function append_bound_service_check(context, service) {
  if (!get_service_id(service)) {
    return;
  }

  context.checks.push(build_bound_service_check(context, service));
}

function append_empty_bound_service_checks(context) {
  if (context.checks.length || !context.configured_services.length) {
    return;
  }

  context.configured_services.forEach(function add_bound(service) {
    append_bound_service_check(context, service);
  });
}

function build_auto_context({
  case_item,
  services = [],
  bound_services = [],
  verification_statuses_by_form = {}
}) {
  return {
    checks: [],
    checks_by_history_key: new Map(),
    service_map: build_service_map(services),
    service_name_map: build_service_name_map(services),
    configured_services: get_configured_services(bound_services),
    configured_service_ids: new Set(),
    verification_statuses_by_form,
    case_item,
    services
  };
}

export function build_auto_verification_checks({
  case_item,
  forms = [],
  verification_logs = [],
  verification_statuses_by_form = {},
  services = [],
  bound_services = [],
  auto_status_snapshot = {},
  auto_history = []
}) {
  const context = build_auto_context({
    case_item,
    services,
    bound_services,
    verification_statuses_by_form
  });
  const normalized_forms = get_normalized_forms(forms);

  normalized_forms.forEach(function add_form_status(form) {
    add_form_status_checks(context, form);
  });

  normalized_forms.forEach(function add_pending_form(form) {
    add_pending_form_checks(context, form);
  });

  verification_logs.forEach(function add_log(log, index) {
    add_verification_log(context, log, index);
  });

  append_auto_ledger_checks(context, auto_status_snapshot, auto_history);
  append_empty_bound_service_checks(context);

  return context.checks;
}
